import {
  CALC_TYPE,
  COUNT_VARIABLE_PREFIX,
  GEO_TYPE_MAPPING,
  label_suppressions,
  registeredParty,
  SOURCE_STATE_DATA_KEY,
} from "./config";
import { safe_division, sortByDateAscending } from "./helpers";
import * as d3 from "d3";
import { nest } from "d3-collection";
import { demographic_sort, getUniqueStates } from "./graphHelpers";
import { NATIONAL_PARAM } from "../dropdowns/urlParams";

// ======= DATA CALCULATION FUNCTIONALITY ========

export function filterData(
  data,
  geo,
  geo_type,
  demoFilters,
  countFields,
  DEMO_MAPPING
) {
  let filtered = data;
  //Filter by Geo Type
  if (geo != null && geo !== "All") {
    filtered = filtered.filter((d) => d[GEO_TYPE_MAPPING[geo_type]] === geo);
  }

  //Filter by Demographics
  for (const filter of demoFilters) {
    if (filter.value !== "All") {
      filtered = filtered.filter(
        (d) => d[DEMO_MAPPING[filter.key]] === filter.value
      );
    }
  }

  let unique_states = getUniqueStates(data);

  //Rollup counts by last selected demographic and COUNT_FIELDs
  let temp_demo = demoFilters[demoFilters.length - 1].key;
  let filteredData = nest()
    .key(function (d) {
      if (unique_states.length > 1) {
        return d[DEMO_MAPPING[temp_demo]] + " - " + d[SOURCE_STATE_DATA_KEY];
      }
      return d[DEMO_MAPPING[temp_demo]];
    })
    .rollup(function (v) {
      let ret = {};
      ret[SOURCE_STATE_DATA_KEY] = v[0][SOURCE_STATE_DATA_KEY];
      countFields.forEach(function (count_field) {
        ret[count_field] = d3.sum(v, function (d) {
          return d[count_field];
        });
      });
      return ret;
    })
    .entries(filtered);
  return filteredData;
}

/**
 * Calculates the total counts for each year category (cy, cyminus1, cyminus2)
 * within the provided dataset. Used for VOTE MODE feature only.
 *
 * @param {Array<Object>} data - The filtered data array where each object has a `key` of category (e.g. Republican, Democrat)
 *     and `value` object with multiple keys corresponding to different vote mode counts
 *     (e.g., `unregistered_cy`, `unregistered_cyminus1`, `unregistered_cyminus2`, `registered_didnt_vote_cy`, `registered_didnt_vote_cyminus1`, etc.).
 * @returns {Array<Object>} An object with the total sums for summed totals of each year by category:
 *     key: "Republican",
 *     value: {
 *         cy: <total for cy>,
 *         cyminus1: <total for cyminus1>,
 *         cyminus2: <total for cyminus2>
 *     }
 */
export const calculateYearlyTotals = (data) => {
  return data.map((category) => {
    const { key, value } = category;
    const summedValues = {
      cy_total: 0,
      cyminus1_total: 0,
      cyminus2_total: 0,
    };

    // Summing up the values based on the year suffix
    for (const yearKey in value) {
      if (yearKey.endsWith("cy") && !yearKey.includes("minus")) {
        summedValues.cy_total += value[yearKey];
      } else if (yearKey.endsWith("cyminus1")) {
        summedValues.cyminus1_total += value[yearKey];
      } else if (yearKey.endsWith("cyminus2")) {
        summedValues.cyminus2_total += value[yearKey];
      }
    }

    // Return the key with the calculated yearly totals
    return { key, value: summedValues };
  });
};

/**
 * Merges yearly total data (cy_total, cyminus1_total, cyminus2_total) from a second object into a
 * base data object. The merge is performed based on the matching "key" field of
 * both objects.
 *
 * @param {Array} filteredData - The main data array containing demographic and voting information.
 * @param {Array} yearlyTotals - The array containing yearly totals (cy_total, cyminus1_total, cyminus2_total)
 *                               for each category to be merged into the base data.
 * @returns {Array} The base data with merged yearly totals.
 */
export const mergeYearlyTotals = (filteredData, yearlyTotals) => {
  const totalsMap = new Map(yearlyTotals.map((item) => [item.key, item.value]));

  return filteredData.map((item) => {
    const correspondingTotals = totalsMap.get(item.key);

    if (correspondingTotals) {
      // Ensure the existing value object is preserved and only totals are added
      item.value = {
        ...item.value, // Keep the existing value properties
        cy_total: correspondingTotals.cy_total,
        cyminus1_total: correspondingTotals.cyminus1_total,
        cyminus2_total: correspondingTotals.cyminus2_total,
      };
    }

    return item;
  });
};

export const handleNoPartyReg = (
  lastSelectedDemo,
  fd,
  setShowNoRegPartyWarning,
  showNoRegPartyWarning
) => {
  // Show no party registration warning
  if (
    lastSelectedDemo === registeredParty &&
    fd.length === 1 &&
    fd[0].key === "Unaffiliated"
  ) {
    setShowNoRegPartyWarning(true);

    // Hack to add all options with a 0 count in registeredParty filter
    let current_values = fd[0].value;
    let add_options = ["Democrat", "Republican", "Other"];
    let zero_values = Object.fromEntries(
      Object.keys(current_values).map((key) => [key, 0])
    );
    let added_data = add_options.map((option) => ({
      key: option,
      value: zero_values,
    }));
    fd.push(...added_data);
  } else if (showNoRegPartyWarning === true) {
    setShowNoRegPartyWarning(false);
  }
};

export function getTotalTurnout(filteredData) {
  let sum_data = {};
  let states = getUniqueStates(filteredData.map((item) => item.value));
  states.forEach((state) => {
    sum_data[state] = {};
    filteredData.forEach((item) => {
      for (const [key, value] of Object.entries(item.value)) {
        if (
          key === SOURCE_STATE_DATA_KEY ||
          (item.value[SOURCE_STATE_DATA_KEY] !== state &&
            item.value[SOURCE_STATE_DATA_KEY] !== undefined)
        ) {
          continue;
        }
        if (key in sum_data[state]) {
          sum_data[state][key] += value;
        } else {
          sum_data[state][key] = value;
        }
      }
    });
  });
  return sum_data;
}

export function calculateGraphData(
  filteredData,
  totalTurnout,
  years,
  voteCountYears,
  demoFilters,
  calc_type,
  vote_mode
) {
  let result = [];
  // Sort the data based on the last selected demographic
  let lastSelectedDemo = getLastSelectedDemo(demoFilters);
  demographic_sort(lastSelectedDemo, filteredData);

  // Get suppressions for the selected demographic
  let suppressions = label_suppressions[lastSelectedDemo] || [];

  // Process each demographic group in the filtered data
  let group = 0;
  filteredData.forEach((d) => {
    if (!suppressions.includes(d.key)) {
      processDemographicGroup(
        d,
        years,
        voteCountYears,
        totalTurnout,
        calc_type,
        vote_mode,
        group,
        result
      );
      group += 1;
    }
  });
  return result;
}

// Get the last selected demographic key
function getLastSelectedDemo(demoFilters) {
  return demoFilters[demoFilters.length - 1].key;
}

// Process a demographic group for every year
function processDemographicGroup(
  d,
  years,
  voteCountYears,
  totalTurnout,
  calc_type,
  vote_mode,
  group,
  result
) {
  years.forEach((y, i) => {
    let calculatedData = calculateYearData(
      d,
      y,
      i,
      voteCountYears,
      totalTurnout,
      calc_type,
      group,
      vote_mode
    );
    result.push(calculatedData);
  });
}

// We need to explicitly map the index to grab the correct 'voteCountYear'
// to avoid this bug when picking comparison years
// https://targetsmart.atlassian.net/browse/SE-1212
const indexYearMapping = {
  cy: 2,
  cyminus1: 1,
  cyminus2: 0,
};

// Calculate the year-specific data for a demographic group
function calculateYearData(
  d,
  year,
  index,
  voteCountYears,
  totalTurnout,
  calc_type,
  group,
  vote_mode
) {
  let state = d["value"][SOURCE_STATE_DATA_KEY] || NATIONAL_PARAM;
  let explicitIndex = indexYearMapping[year];
  let voteCountYear = vote_mode
    ? `${year}_total`
    : voteCountYears[COUNT_VARIABLE_PREFIX + explicitIndex];
  let registeredCountYear = `registered_count_${year}`;
  let voteCounts = d["value"][voteCountYear];
  let totalRegistered = d["value"][registeredCountYear];
  let totalTurnoutByYear = totalTurnout[state][voteCountYear];

  let value =
    calc_type === CALC_TYPE.TURNOUT_PERCENT
      ? safe_division(voteCounts, totalRegistered)
      : safe_division(voteCounts, totalTurnoutByYear);

  let calculatedData = {
    year: year,
    index: index,
    key: d["key"],
    [SOURCE_STATE_DATA_KEY]: state,
    vote_count: voteCounts,
    total_turnout: totalTurnoutByYear,
    total_registered: totalRegistered,
    value: value,
    group: group,
  };

  // If vote_mode is enabled, add stacked bar data
  if (vote_mode) {
    addStackedBarData(d, year, totalTurnoutByYear, calculatedData);
  }

  return calculatedData;
}

// Add stacked bar data to the calculated data for vote_mode

function addStackedBarData(d, year, totalTurnoutByYear, calculatedData) {
  // This is ugly but necessary from what I can tell
  // Since dynamic value generation in a dictionary did not work

  // Raw counts
  let in_person_early_count = d["value"][`in_person_early_count_${year}`];
  let absentee_count = d["value"][`absentee_count_${year}`];
  let on_eday_count = d["value"][`on_eday_count_${year}`];
  let registered_didnt_vote_count = d["value"][`registered_didnt_vote_${year}`];
  let unregistered_count = d["value"][`unregistered_${year}`];

  // Percentages (normalized values)
  let in_person_early_value = safe_division(
    in_person_early_count,
    totalTurnoutByYear
  );
  let absentee_value = safe_division(absentee_count, totalTurnoutByYear);
  let on_eday_value = safe_division(on_eday_count, totalTurnoutByYear);
  let registered_didnt_vote_value = safe_division(
    registered_didnt_vote_count,
    totalTurnoutByYear
  );
  let unregistered_value = safe_division(
    unregistered_count,
    totalTurnoutByYear
  );

  // Store both raw counts and percentages in the calculatedData object
  calculatedData.in_person_early_count = in_person_early_count;
  calculatedData.absentee_count = absentee_count;
  calculatedData.on_eday_count = on_eday_count;
  calculatedData.registered_didnt_vote_count = registered_didnt_vote_count;
  calculatedData.unregistered_count = unregistered_count;

  calculatedData.in_person_early_value = in_person_early_value;
  calculatedData.absentee_value = absentee_value;
  calculatedData.on_eday_value = on_eday_value;
  calculatedData.registered_didnt_vote_value = registered_didnt_vote_value;
  calculatedData.unregistered_value = unregistered_value;
}

export function calculateRollupYearComparisons(
  data,
  years,
  currentEavCountPrefix,
  daysOutDateType
) {
  var yearlyRollup = nest()
    .key(function (d) {
      return d.State || NATIONAL_PARAM;
    })
    .key(function (d) {
      return d.Date_Voted;
    })
    .rollup(function (d) {
      let ret = {};
      years.forEach((y, i) => {
        ret["voters_" + i] = d3.sum(d, function (e) {
          return e[currentEavCountPrefix + y];
        });
      });
      return ret;
    })
    .entries(data);

  if (!daysOutDateType) {
    yearlyRollup.forEach(function (d) {
      d.key = new Date(d.key);
    });
  }
  yearlyRollup = yearlyRollup.sort(sortByDateAscending);
  return yearlyRollup;
}

export function calculateRollupModeledParty(
  data,
  countField,
  DEMO_MAPPING,
  daysOutDateType
) {
  var rollup = nest()
    .key(function (d) {
      return d.State || NATIONAL_PARAM;
    })
    .key(function (d) {
      return d.Date_Voted;
    })
    .rollup(function (v) {
      return {
        dem_voters: d3.sum(v, function (d) {
          if (d[DEMO_MAPPING["modeledParty"]] === "Democrat") {
            return d[countField];
          }
        }),
        gop_voters: d3.sum(v, function (d) {
          if (d[DEMO_MAPPING["modeledParty"]] === "Republican") {
            return d[countField];
          }
        }),
        other_voters: d3.sum(v, function (d) {
          if (d[DEMO_MAPPING["modeledParty"]] === "Unaffiliated") {
            return d[countField];
          }
        }),
      };
    })
    .entries(data);

  if (!daysOutDateType) {
    rollup.forEach(function (d) {
      d.key = new Date(d.key);
    });
  }
  rollup = rollup.sort(sortByDateAscending);
  return rollup;
}
